import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { exceptionRanges as exceptionRangesApi } from '../../../../../requests/api/apiClient';
import {
  CultivationCyclePopulatedSchema, GetSectorResumeDataResponse,
  PhenologicalStateWithCycle, SectorDetailSchema,
} from '../../../../../requests/api/apiTypes';
import { INTERVALS } from '../types';
import { getIrrigationValueFromSectorInfo, InheritedConfig } from '../utils';
import { calculateChartRangeStats } from '../utils/chartRangeStats';
import { useChartRulerContext } from '../../../../../context/ChartRulerContext';
import { useChartExceptionContext, ExceptionRange } from '../../../../../context/ChartExceptionContext';
import { useDashboardDownloadContext } from '../../../../../context/DashboardDownloadContext';
import CollapsibleSection, { RULER_METRIC_OPTIONS, RulerMetricOption } from './CollapsibleSection';

type TimeInterval = '1-day' | '1-week' | '2-weeks' | '1-month' | '3-months' | '6-months' | null;

const EXCEPTION_DATE_FORMAT = 'DD/MM/YYYY HH:mm';

const formatExceptionRangeLabel = (range: ExceptionRange): string => {
  const start = dayjs.utc(range.startDate);
  const end = dayjs.utc(range.endDate);
  if (range.chart) {
    const center = (start.valueOf() + end.valueOf()) / 2;
    return `Punto: ${dayjs.utc(center).format(EXCEPTION_DATE_FORMAT)}`;
  }
  return `${start.format(EXCEPTION_DATE_FORMAT)} — ${end.format(EXCEPTION_DATE_FORMAT)}`;
};

interface FilterSidebarProps {
  inheritedConfig: InheritedConfig;
  sectorInfo: SectorDetailSchema;
  selectedVarietyName: string;
  selectedSpecies: string;
  value: string;
  dashboardType: number;
  selectedInterval: string;
  tempStartDate: string;
  tempEndDate: string;
  selectedCultivationCycle: string;
  selectedPhenologicalState: string;
  cultivationCycles: CultivationCyclePopulatedSchema[];
  phenologicalStatesByCycle: PhenologicalStateWithCycle;
  onDashboardTypeChange: (value: number) => void;
  onIntervalSelect: (interval: TimeInterval) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onCultivationCycleChange: (cycleId: string) => void;
  onPhenologicalStateChange: (stateId: string) => void;
  onApplyDates: () => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  inheritedConfig,
  sectorInfo,
  selectedVarietyName,
  selectedSpecies,
  value,
  dashboardType,
  selectedInterval,
  tempStartDate,
  tempEndDate,
  selectedCultivationCycle,
  selectedPhenologicalState,
  cultivationCycles,
  phenologicalStatesByCycle,
  onDashboardTypeChange,
  onIntervalSelect,
  onStartDateChange,
  onEndDateChange,
  onCultivationCycleChange,
  onPhenologicalStateChange,
  onApplyDates,
  collapsed,
  setCollapsed,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  const [sectorResumeData, setSectorResumeData] = useState<GetSectorResumeDataResponse['sectorResumeData'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartsReadyKey, setChartsReadyKey] = useState(0);
  const [selectedRulerMetric, setSelectedRulerMetric] = useState<RulerMetricOption>('valueDiff');
  const [savingException, setSavingException] = useState(false);
  const chartRulerContext = useChartRulerContext();
  const chartExceptionContext = useChartExceptionContext();
  const dashboardDownloadContext = useDashboardDownloadContext();

  useEffect(() => {
    const id = setTimeout(() => setChartsReadyKey((k) => k + 1), 0);
    return () => clearTimeout(id);
  }, [dashboardType]);

  const rulerRangeStats = useMemo(() => {
    const [p1, p2] = chartRulerContext?.rulerPoints ?? [null, null];
    if (p1 == null || p2 == null || !dashboardDownloadContext?.getRegisteredCharts) return null;
    return calculateChartRangeStats(
      dashboardDownloadContext.getRegisteredCharts,
      p1,
      p2,
    );
  }, [
    chartRulerContext?.rulerPoints,
    dashboardDownloadContext,
    dashboardType,
    chartsReadyKey,
  ]);

  const rulerRangeItemsGrouped = useMemo(() => {
    if (!rulerRangeStats?.bySeries.length) return [];
    type ByChart = Record<string, typeof rulerRangeStats.bySeries>;
    const byChart = rulerRangeStats.bySeries.reduce<ByChart>(
      (acc, series) => {
        const key = series.chartTitle;
        if (!acc[key]) acc[key] = [];
        acc[key].push(series);
        return acc;
      },
      {},
    );
    return Object.entries(byChart).flatMap(([chartTitle, seriesList]) => {
      const headerItem = { label: chartTitle, value: '', isHeader: true };
      const seriesItems = seriesList.map((series) => {
        const metricValue = {
          valueDiff: series.valueDiff.toFixed(2),
          promedio: series.promedio.toFixed(2),
          min: series.min.toFixed(2),
          max: series.max.toFixed(2),
        }[selectedRulerMetric];
        return { label: series.seriesName, value: metricValue, isHeader: false };
      });
      return [headerItem, ...seriesItems];
    });
  }, [rulerRangeStats, selectedRulerMetric]);

  useEffect(() => {
    if (sectorInfo.id) {
      const getSectorResumeData = async () => {
        setLoading(true);
        const response = await apiClient.sector.getSectorResumeData(
          sectorInfo.id as string,
          dayjs(tempStartDate).utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
          dayjs(tempEndDate).utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        );
        setSectorResumeData(response.data?.sectorResumeData);
        setLoading(false);
      };
      getSectorResumeData();
    }
  }, [tempStartDate, tempEndDate, sectorInfo]);

  useEffect(() => {
    if (!sectorInfo.id || !chartExceptionContext) return;
    exceptionRangesApi.get(sectorInfo.id as string).then((res) => {
      type Resp = { data?: { exceptionRanges?: [] } };
      chartExceptionContext.setExceptionRanges((res as Resp).data?.exceptionRanges || []);
    }).catch(() => {});
  }, [sectorInfo.id]);

  const handleSaveException = async () => {
    if (!sectorInfo.id || !chartExceptionContext) return;
    const { pendingRanges } = chartExceptionContext;
    if (!pendingRanges.length) return;
    const newRanges = pendingRanges.map(([p1, p2]) => ({
      startDate: dayjs(Math.min(p1, p2)).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      endDate: dayjs(Math.max(p1, p2)).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    }));
    const updated = [...chartExceptionContext.exceptionRanges, ...newRanges];
    setSavingException(true);
    try {
      await chartExceptionContext.persistExceptionRanges(sectorInfo.id as string, updated);
      chartExceptionContext.clearPendingRanges();
      if (chartExceptionContext.isExceptionToolActive) {
        chartExceptionContext.activateExceptionTool();
      }
    } finally {
      setSavingException(false);
    }
  };

  const handleDeleteException = async (index: number) => {
    if (!sectorInfo.id || !chartExceptionContext) return;
    const updated = chartExceptionContext.exceptionRanges.filter((_, i) => i !== index);
    await chartExceptionContext.persistExceptionRanges(sectorInfo.id as string, updated);
  };

  return (<Box
    sx={{
      position: 'fixed',
      top: 64,
      right: 0,
      width: collapsed ? '50px' : '350px',
      minWidth: collapsed ? '50px' : '350px',
      height: 'calc(100vh - 64px)',
      transition: 'width 0.2s, min-width 0.2s',
      background: collapsed ? 'transparent' : '#f5f6fa',
      borderLeft: collapsed ? 'none' : '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
    }}
  >
    <IconButton
      onClick={() => setCollapsed(!collapsed)}
      size="small"
      sx={{
        position: 'absolute',
        top: 65,
        left: collapsed ? 8 : -16,
        background: '#fff',
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        zIndex: 1001,
        width: 32,
        height: 32,
        transition: 'left 0.2s',
        '&:hover': {
          background: '#f5f6fa',
        },
      }}
    >
      {collapsed ? <ChevronLeft /> : <ChevronRight />}
    </IconButton>
    {!collapsed && (
      <Box sx={{
        flex: 1,
        maxHeight: '100%',
        width: '100%',
        p: 3,
        pt: 0,
        paddingTop: '20px',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'white',
      }}>
        <Box display="flex" flexDirection="column" gap={0.5}>
          <CollapsibleSection
            title="Filtros de visualización"
            defaultExpanded={true}
            items={[]}
            renderBeforeItems={(
              <Box display="flex" flexDirection="column" gap={0.5}>
                {value === '1' && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                      Visualizaciones
                    </Typography>
                    <TextField
                      value={dashboardType}
                      select
                      onChange={(e) => onDashboardTypeChange(Number(e.target.value))}
                      variant="standard"
                      fullWidth
                    >
                      <MenuItem value={1}>Suelo</MenuItem>
                      <MenuItem value={2}>Planta</MenuItem>
                      <MenuItem value={3}>Humedad + Temperatura + Salinidad</MenuItem>
                      <MenuItem value={4}>Clima</MenuItem>
                      <MenuItem value={5}>Suelo (Todos)</MenuItem>
                      <MenuItem value={6}>Planta (Todos)</MenuItem>
                      <MenuItem value={7}>Riego (Todos)</MenuItem>
                      <MenuItem value={8}>Clima (Todos)</MenuItem>
                    </TextField>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                    Intervalos de
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {INTERVALS.map((interval) => (
                      <Chip
                        key={interval.key}
                        color={selectedInterval === interval.key ? 'primary' : undefined}
                        label={interval.label}
                        onClick={() => onIntervalSelect(interval.key as TimeInterval)}
                        size='small'
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                    Ciclo de cultivo
                  </Typography>
                  <TextField
                    value={selectedCultivationCycle}
                    select
                    onChange={(e) => onCultivationCycleChange(e.target.value)}
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">
                      Sin seleccionar
                    </MenuItem>
                    {cultivationCycles.map((cycle) => (
                      <MenuItem key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {selectedCultivationCycle
                  && phenologicalStatesByCycle[selectedCultivationCycle]
                  && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                      Estado fenológico
                    </Typography>
                    <TextField
                      value={selectedPhenologicalState}
                      select
                      onChange={(e) => onPhenologicalStateChange(e.target.value)}
                      variant="standard"
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">
                        Sin seleccionar
                      </MenuItem>
                      {phenologicalStatesByCycle[selectedCultivationCycle]?.map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  )}

                <Box display="flex" flexDirection="row" gap={0.5} justifyContent="space-between">
                  <Box width="50%">
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                      Fecha de inicio
                    </Typography>
                    <TextField
                      type="date"
                      value={tempStartDate}
                      onChange={(e) => onStartDateChange(e.target.value)}
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        max: tempEndDate || today,
                      }}
                      error={Boolean(tempStartDate && tempEndDate && tempStartDate > tempEndDate)}
                      helperText={tempStartDate && tempEndDate && tempStartDate > tempEndDate ? 'La fecha de inicio no puede ser mayor a la fecha de fin' : ''}
                    />
                  </Box>
                  <Box width="50%">
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0 }}>
                      Fecha de fin
                    </Typography>
                    <TextField
                      type="date"
                      value={tempEndDate}
                      onChange={(e) => onEndDateChange(e.target.value)}
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: tempStartDate || undefined,
                        max: today,
                      }}
                      error={Boolean(tempStartDate && tempEndDate && tempStartDate > tempEndDate)}
                      helperText={tempStartDate && tempEndDate && tempStartDate > tempEndDate ? 'La fecha de fin no puede ser menor a la fecha de inicio' : ''}
                    />
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onApplyDates}
                  disabled={Boolean(tempStartDate && tempEndDate && tempStartDate > tempEndDate)}
                  fullWidth
                  sx={{ mt: 0.25 }}
                  size="small"
                >
                  APLICAR
                </Button>
              </Box>
            )}
          />
          <Divider />
          <Box display="flex" flexDirection="column" gap={0}>
            <CollapsibleSection
              title="Información del sector"
              defaultExpanded={false}
              items={[
                { label: 'Superficie:', value: `${sectorInfo?.theoreticalSurface ?? '-'} HA` },
                { label: 'Especie/variedad:', value: `${selectedSpecies} / ${selectedVarietyName}` },
                { label: 'Año de plantación:', value: sectorInfo?.plantingYear ?? '-' },
                {
                  label: 'N° de emisores por hectáreas:',
                  value: getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emittersPerHectare') ?? '-',
                },
              ]}
            />
            <Divider sx={{ my: 0.5 }} />
            <CollapsibleSection
              title="Diseño de riego"
              defaultExpanded={false}
              items={[
                {
                  label: 'Tipo de riego:',
                  value: getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'typeOfIrrigation') || '-',
                },
                {
                  label: 'Precipitación teórica:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'theoreticalPrecipitation') || '-'} mm/h`,
                },
                {
                  label: 'Caudal Emisor:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emitterFlow') || '-'} lt/h`,
                },
                {
                  label: 'N° de líneas o Aspersión:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'lineNumber') || '-'} unidades`,
                },
                {
                  label: 'Número de emisores por planta:',
                  value: (() => {
                    const perHectare = getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'plantsPerHectare');
                    const emitters = getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emittersPerHectare');
                    if (!perHectare || !emitters || Number(perHectare) === 0) return '-';
                    return `${Math.round(Number(emitters) / Number(perHectare))} unidades`;
                  })(),
                },
                {
                  label: 'Superficie por emisor:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emitterSurface') || '-'} HA`,
                },
              ]}
            />
            <Divider sx={{ my: 0.5 }} />
            <CollapsibleSection
              title="Diseño plantación"
              defaultExpanded={false}
              items={[
                {
                  label: 'N° de plantas por hectáreas:',
                  value: getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'plantsPerHectare') ?? '-',
                },
                {
                  label: 'Distancia entre plantas:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'plantDistance') ?? '-'} m`,
                },
                {
                  label: 'Distancia entre hilera:',
                  value: `${getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'rowDistance') ?? '-'} m`,
                },
                {
                  label: 'Marco de plantación:',
                  value: `${sectorInfo?.plantingFrame ?? '-'}`,
                },
              ]}
            />
            <Divider sx={{ my: 0.5 }} />
            <CollapsibleSection
              title="Datos periodo riego"
              defaultExpanded={true}
              items={[
                { label: 'Estados fenológicos:', value: sectorResumeData?.phenologicalStatesNames?.join(', ') ?? 'N/A' },
                { label: 'Riego acumulado del periodo:', value: `${sectorResumeData?.irrigationAccumulatedResult?.toFixed(2) ?? 'N/A'} mm` },
                { label: 'Precipitación acum. del periodo:', value: `${sectorResumeData?.rainAccumulatedResult?.toFixed(2) ?? 'N/A'} mm` },
                { label: 'ETo acumulado del periodo:', value: `${sectorResumeData?.et0AccumulatedResult?.toFixed(2) ?? 'N/A'} mm` },
                { label: 'Kc riego del periodo:', value: sectorResumeData?.kcIrrigation?.toFixed(2) ?? 'N/A' },
                { label: 'Riego real / día:', value: `${sectorResumeData?.irrigationPerDay?.toFixed(2) ?? 'N/A'} mm` },
              ]}
              loading={loading}
            />
            {rulerRangeStats && rulerRangeStats.bySeries.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <CollapsibleSection
                  title="Datos entre el rango seleccionado"
                  defaultExpanded={false}
                  renderBeforeItems={(
                    <Box sx={{ mb: 1 }}>
                      <TextField
                        select
                        size="small"
                        value={selectedRulerMetric}
                        onChange={(e) => setSelectedRulerMetric(
                          e.target.value as RulerMetricOption,
                        )}
                        variant="standard"
                        fullWidth
                        label="Visualizando"
                      >
                        {RULER_METRIC_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}
                  items={rulerRangeItemsGrouped}
                />
              </>
            )}
            {chartExceptionContext && (
              chartExceptionContext.pendingRanges.length > 0
              || chartExceptionContext.exceptionRanges.length > 0
            ) && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <CollapsibleSection
                  title="Excepciones de datos"
                  defaultExpanded={true}
                  items={[]}
                  renderBeforeItems={(
                    <Box display="flex" flexDirection="column" gap={1}>
                      {chartExceptionContext.pendingRanges.length > 0 && (
                        <Box sx={{
                          border: '1px solid rgba(255,140,0,0.4)',
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: 'rgba(255,140,0,0.05)',
                        }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            {chartExceptionContext.pendingRanges.length === 1
                              ? 'Rango pendiente:'
                              : `${chartExceptionContext.pendingRanges.length} rangos pendientes:`}
                          </Typography>
                          {chartExceptionContext.pendingRanges.map(([p1, p2], i) => (
                            <Typography key={`pending-${i}`} variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {dayjs(Math.min(p1, p2)).utc().format('DD/MM/YYYY HH:mm')}
                              {' — '}
                              {dayjs(Math.max(p1, p2)).utc().format('DD/MM/YYYY HH:mm')}
                            </Typography>
                          ))}
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            disabled={savingException}
                            onClick={handleSaveException}
                            sx={{ mt: 0.5, backgroundColor: '#ff8c00', '&:hover': { backgroundColor: '#e67e00' } }}
                          >
                            Guardar {chartExceptionContext.pendingRanges.length === 1 ? 'excepción' : 'excepciones'}
                          </Button>
                        </Box>
                      )}
                      {chartExceptionContext.exceptionRanges.map((range, i) => (
                        <Box
                          key={`exc-${i}`}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            border: '1px solid rgba(160,160,160,0.3)',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(160,160,160,0.08)',
                          }}
                        >
                          <Box display="flex" flexDirection="column">
                            <Typography variant="caption">
                              {formatExceptionRangeLabel(range)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '10px', color: range.chart ? '#0EA5E9' : '#64748B' }}
                            >
                              {range.chart || 'Todo el sector'}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteException(i)}
                            sx={{ p: '2px', ml: 0.5 }}
                          >
                            <Typography variant="caption" sx={{ lineHeight: 1 }}>✕</Typography>
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    )}
  </Box>);
};

export default FilterSidebar;
